import { memo } from 'react';

import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentActionsMenuAddReferenceRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/WorkspaceAgentActionsMenuAddReferenceRow';
import { WorkspaceAgentActionsMenuEmptyState } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/WorkspaceAgentActionsMenuEmptyState';
import { WorkspaceAgentActionsMenuGroupRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/WorkspaceAgentActionsMenuGroupRow';
import { WorkspaceAgentActionsMenuListRowSkeletonList } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/WorkspaceAgentActionsMenuListRowSkeletonList';
import { WorkspaceAgentActionsMenuSavedSkillsRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/WorkspaceAgentActionsMenuSavedSkillsRow';
import { WorkspaceAgentActionsMenuSkillRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/WorkspaceAgentActionsMenuSkillRow';
import { WorkspaceAgentActionsMenuTaskRow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/WorkspaceAgentActionsMenuTaskRow';
import { type WorkspaceAgentActionsMenuScrollBodyProps } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/actions-menu/workspaceAgentActionsMenuPanelSession';
import {
  WORKSPACE_AGENT_ACTIONS_MENU_EMPTY_SEARCH_MESSAGE,
  WORKSPACE_AGENT_ACTIONS_MENU_EMPTY_TASKS_MESSAGE,
  WORKSPACE_AGENT_ACTIONS_MENU_SEARCH_PLACEHOLDER_WITH_SKILLS,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentActionsMenuLayout';
import {
  WORKSPACE_AGENT_ACTIONS_MENU_SKILLS_SEARCH_LOADING_SKELETON_ROW_COUNT,
  WORKSPACE_AGENT_ACTIONS_MENU_SKILLS_SEARCH_LOADING_SKELETON_ROW_COUNT_WHEN_TASKS_VISIBLE,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentActionsMenuSkillsLayout';

export const WorkspaceAgentActionsMenuScrollBody = memo(
  ({
    addReferenceLabel,
    fillsActionsMenuPopoverHeight,
    handleAddReference,
    handleOpenSavedSkills,
    handleSelectGroup,
    handleSelectSkill,
    handleSelectTask,
    isAddReferenceVisible,
    isBackRowVisible,
    isBlendedSkillsSearchLoading,
    isSavedSkillsEntryVisible,
    isSearching,
    searchPlaceholder,
    showEmpty,
    visibleBlendedSearchSkills,
    visibleGroups,
    visibleTasks,
  }: WorkspaceAgentActionsMenuScrollBodyProps) => {
    const emptySearchMessage =
      searchPlaceholder === WORKSPACE_AGENT_ACTIONS_MENU_SEARCH_PLACEHOLDER_WITH_SKILLS
        ? WORKSPACE_AGENT_ACTIONS_MENU_EMPTY_SEARCH_MESSAGE
        : WORKSPACE_AGENT_ACTIONS_MENU_EMPTY_TASKS_MESSAGE;

    return (
      <div
        className={cn(
          'flex min-h-0 flex-col gap-1',
          showEmpty ? 'flex-1 overflow-y-auto p-1.5' : 'px-1.5 pb-2',
          !showEmpty && (isBackRowVisible ? 'pt-1' : 'pt-2'),
          fillsActionsMenuPopoverHeight ? 'flex-1 overflow-y-auto' : 'shrink-0',
        )}
      >
        {showEmpty ? <WorkspaceAgentActionsMenuEmptyState message={emptySearchMessage} /> : null}
        {!showEmpty &&
          visibleGroups.map((group) => (
            <WorkspaceAgentActionsMenuGroupRow
              description={group.description}
              groupId={group.id}
              icon={group.icon}
              iconColorClassName={group.iconColorClassName}
              key={group.id}
              label={group.label}
              onSelect={handleSelectGroup}
            />
          ))}
        {!showEmpty &&
          visibleTasks.map((task) => (
            <WorkspaceAgentActionsMenuTaskRow key={task.id} onSelect={handleSelectTask} task={task} />
          ))}
        {!showEmpty && isSearching && isBlendedSkillsSearchLoading && visibleBlendedSearchSkills.length === 0 ? (
          <WorkspaceAgentActionsMenuListRowSkeletonList
            data-testid="WORKSPACE_AGENT_ACTIONS_MENU_BLENDED_SKILLS_STATUS"
            layout="search"
            loadingLabel="Searching skills"
            rowCount={
              visibleTasks.length > 0
                ? WORKSPACE_AGENT_ACTIONS_MENU_SKILLS_SEARCH_LOADING_SKELETON_ROW_COUNT_WHEN_TASKS_VISIBLE
                : WORKSPACE_AGENT_ACTIONS_MENU_SKILLS_SEARCH_LOADING_SKELETON_ROW_COUNT
            }
          />
        ) : null}
        {!showEmpty &&
          visibleBlendedSearchSkills.map((skill) => (
            <WorkspaceAgentActionsMenuSkillRow
              key={skill.id}
              layout="search"
              onSelect={handleSelectSkill}
              skill={skill}
            />
          ))}
        {!showEmpty && (isAddReferenceVisible || isSavedSkillsEntryVisible) ? (
          <div className="mt-1 ads:border-default border-gray-4 border-t pt-1">
            {isAddReferenceVisible ? (
              <WorkspaceAgentActionsMenuAddReferenceRow label={addReferenceLabel} onAddReference={handleAddReference} />
            ) : null}
            {isSavedSkillsEntryVisible ? (
              <WorkspaceAgentActionsMenuSavedSkillsRow onSelect={handleOpenSavedSkills} />
            ) : null}
          </div>
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentActionsMenuScrollBody.displayName = 'WorkspaceAgentActionsMenuScrollBody';
